import type { Stage } from '../types';

export type ProfileMode = 'pregnant' | 'postpartum' | 'unset';

export interface PregnancySnapshot {
  mode: ProfileMode;
  /** 妊娠週数（0始まりではなく臨床週数。例: 12 = 12週） */
  gestationalWeeks: number | null;
  gestationalDays: number | null;
  /** 出産予定日までの日数（負なら超過） */
  daysUntilDue: number | null;
  /** 産後日数 */
  daysSinceBirth: number | null;
  /** 産後月齢（おおよそ） */
  postpartumMonths: number | null;
  stageId: string;
  label: string;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function parseDateInput(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return date;
}

export function formatDateInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function daysBetween(from: Date, to: Date): number {
  const a = startOfLocalDay(from).getTime();
  const b = startOfLocalDay(to).getTime();
  return Math.round((b - a) / MS_PER_DAY);
}

/** 出産予定日から最終月経開始日（LMP）を推定（280日前） */
export function dueDateToLmp(dueDate: Date): Date {
  const lmp = new Date(dueDate);
  lmp.setDate(lmp.getDate() - 280);
  return lmp;
}

export function gestationalAgeFromDueDate(
  dueDate: Date,
  today: Date = new Date()
): { weeks: number; days: number; totalDays: number } {
  const lmp = dueDateToLmp(dueDate);
  const totalDays = Math.max(0, daysBetween(lmp, today));
  return {
    weeks: Math.floor(totalDays / 7),
    days: totalDays % 7,
    totalDays,
  };
}

export function resolveStageId(snapshot: {
  mode: ProfileMode;
  gestationalWeeks: number | null;
  daysSinceBirth: number | null;
}): string {
  if (snapshot.mode === 'postpartum' && snapshot.daysSinceBirth != null) {
    if (snapshot.daysSinceBirth < 90) return 'postpartum-early';
    return 'postpartum-late';
  }
  if (snapshot.mode === 'pregnant' && snapshot.gestationalWeeks != null) {
    const w = snapshot.gestationalWeeks;
    if (w <= 13) return 'pregnancy-1';
    if (w <= 27) return 'pregnancy-2';
    if (w <= 36) return 'pregnancy-3';
    return 'birth';
  }
  return 'pregnancy-1';
}

export function buildSnapshot(options: {
  dueDate: string | null;
  birthDate: string | null;
  today?: Date;
  locale?: 'ja' | 'en' | 'zh';
}): PregnancySnapshot {
  const today = options.today ?? new Date();
  const locale = options.locale ?? 'ja';

  if (options.birthDate) {
    const birth = parseDateInput(options.birthDate);
    if (birth) {
      const daysSinceBirth = Math.max(0, daysBetween(birth, today));
      const postpartumMonths = Math.floor(daysSinceBirth / 30.44);
      const mode: ProfileMode = 'postpartum';
      const stageId = resolveStageId({ mode, gestationalWeeks: null, daysSinceBirth });
      return {
        mode,
        gestationalWeeks: null,
        gestationalDays: null,
        daysUntilDue: null,
        daysSinceBirth,
        postpartumMonths,
        stageId,
        label: formatPostpartumLabel(daysSinceBirth, postpartumMonths, locale),
      };
    }
  }

  if (options.dueDate) {
    const due = parseDateInput(options.dueDate);
    if (due) {
      const age = gestationalAgeFromDueDate(due, today);
      const daysUntilDue = daysBetween(today, due);
      // 予定日を大幅に過ぎたら産後モードに近い出生ステージのまま案内
      const mode: ProfileMode = 'pregnant';
      const stageId = resolveStageId({
        mode,
        gestationalWeeks: age.weeks,
        daysSinceBirth: null,
      });
      return {
        mode,
        gestationalWeeks: age.weeks,
        gestationalDays: age.days,
        daysUntilDue,
        daysSinceBirth: null,
        postpartumMonths: null,
        stageId,
        label: formatPregnantLabel(age.weeks, age.days, daysUntilDue, locale),
      };
    }
  }

  return {
    mode: 'unset',
    gestationalWeeks: null,
    gestationalDays: null,
    daysUntilDue: null,
    daysSinceBirth: null,
    postpartumMonths: null,
    stageId: 'pregnancy-1',
    label: '',
  };
}

function formatPregnantLabel(
  weeks: number,
  days: number,
  daysUntilDue: number,
  locale: 'ja' | 'en' | 'zh'
): string {
  if (locale === 'en') {
    const due =
      daysUntilDue >= 0
        ? `${daysUntilDue} days until due date`
        : `${Math.abs(daysUntilDue)} days past due date`;
    return `Week ${weeks}+${days} · ${due}`;
  }
  if (locale === 'zh') {
    const due =
      daysUntilDue >= 0
        ? `距预产期还有 ${daysUntilDue} 天`
        : `已过预产期 ${Math.abs(daysUntilDue)} 天`;
    return `孕 ${weeks} 周 ${days} 天 · ${due}`;
  }
  const due =
    daysUntilDue >= 0
      ? `出産予定日まであと${daysUntilDue}日`
      : `予定日を${Math.abs(daysUntilDue)}日超過`;
  return `妊娠${weeks}週${days}日 · ${due}`;
}

function formatPostpartumLabel(
  daysSinceBirth: number,
  postpartumMonths: number,
  locale: 'ja' | 'en' | 'zh'
): string {
  if (locale === 'en') {
    return `${daysSinceBirth} days postpartum · about ${postpartumMonths} months`;
  }
  if (locale === 'zh') {
    return `产后第 ${daysSinceBirth} 天 · 约 ${postpartumMonths} 个月`;
  }
  return `産後${daysSinceBirth}日目 · 約${postpartumMonths}か月`;
}

export function findStage(stages: Stage[], stageId: string): Stage {
  return stages.find((s) => s.id === stageId) ?? stages[0];
}

/** フォーカス候補の優先カテゴリ */
const FOCUS_PRIORITY = ['admin', 'hospital', 'body', 'shopping', 'life', 'mind', 'partner'] as const;

export function pickFocusItems(
  stage: Stage,
  isChecked: (stageId: string, itemId: string) => boolean,
  limit = 5
): Stage['checklist'] {
  const unchecked = stage.checklist.filter((item) => !isChecked(stage.id, item.id));
  unchecked.sort((a, b) => {
    const ai = FOCUS_PRIORITY.indexOf(a.category as (typeof FOCUS_PRIORITY)[number]);
    const bi = FOCUS_PRIORITY.indexOf(b.category as (typeof FOCUS_PRIORITY)[number]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  return unchecked.slice(0, limit);
}
